<script setup>
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useSettingsStore } from '@/store/settings'
import { computed, onMounted, ref, watch } from 'vue'
import { useInstanceStore } from '@/store/backendInstances'
import { useBranchString, isInDataCollectorMode, useProductName } from '@/composables/version'
import { useDisplay, useTheme } from 'vuetify'
import LanguageSelector from '@/components/settings/LanguageSelector.vue'
import { useMiscellaneousStore } from '@/store/miscellaneous'
import EulaCheckbox from '@/components/EulaCheckbox.vue'
import BackendAPI from '@/BackendAPI.js'
import { useEphemeralBackendInstancesStore } from '@/store/ephemeralBackendInstances.js'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'
import SimulatedPermissionPicker from '@/components/SimulatedPermissionPicker.vue'
import { setupPullToRefresh } from '@/composables/usePullToRefresh'
import { isNative } from '@/composables/capacitor.js'
import useBackgroundPattern from '@/composables/themes/useBackgroundPattern'
import { hexToRGBA } from '@/composables/helperFunctions.js'
import QueueInvestigationCard from '@/components/QueueInvestigationCard.vue'

const instanceStore = useInstanceStore()
const router = useRouter()
const route = useRoute()
const { mobile } = useDisplay()
const { t, locale } = useI18n({ useScope: 'global' })
const miscellaneousStore = useMiscellaneousStore()
const settingsStore = useSettingsStore()
const theme = useTheme()
const { backgroundImage } = useBackgroundPattern()
const { isRefreshing, refreshValue, pullRefreshElement } = setupPullToRefresh()

const showEulaDialog = ref(false)
const hasAcceptedEULA = ref(false)
const acceptedEula = ref(null)
const eulaLoading = ref(false)
const closeTimer = ref(3)

const versionWarning = ref(false)
const versionWarningTitle = ref('')
const versionWarningText = ref('')
const versionWarningColor = ref('')
const showWelcomeDialog = ref(false)
const showLanguageDialog = ref(false)
const showPermissionSimulationDialog = ref(false)
const isDataCollector = computed(isInDataCollectorMode)

const needToRefreshEula = computed(() => {
  if (instanceStore.getSenator && useEphemeralBackendInstancesStore().eulaRefreshRequired[instanceStore.getSenator.globalUniqueIdentifier] === true)
    return true
  if (!instanceStore.selectedInstanceId) return false
  return useEphemeralBackendInstancesStore().eulaRefreshRequired[instanceStore.selectedInstanceId] === true
})

const canShowWelcomeDialog = computed(() => {
  if (showLanguageDialog.value) return false
  if (notOnInstanceSelection.value) return false
  if (
    isDataCollector.value &&
    instanceStore.globalUniqueIdentifiers.filter(
      globalUniqueIdentifier => instanceStore.instances[globalUniqueIdentifier].instanceType === 'data_collector'
    ).length !== 0
  )
    return false
  if (!isDataCollector.value && instanceStore.globalUniqueIdentifiersForBarns.length !== 0) return false
  return !settingsStore.genericInstancesEnabled
})

const showGlobalAppBar = computed(() => {
  return !useInstanceStore().selectedInstanceId
})

const notOnInstanceSelection = computed(() => {
  return route.name !== 'Instances'
})

const displayBackButton = computed(() => {
  return route.meta?.backButton === true
})

const isSimulatingPermissions = computed(() => {
  return settingsStore.isPermissionSimulationEnabled
})

async function acceptEula() {
  eulaLoading.value = true
  if (!acceptedEula.value) return
  await BackendAPI.getOrCreateBackendAPIInstance(eulaTargetGUID.value).acceptEULA(acceptedEula.value)
  router.go(0)
  eulaLoading.value = false
}

function canCloseTimerFunction() {
  if (closeTimer.value <= 0) return
  setTimeout(() => {
    closeTimer.value -= 1
    canCloseTimerFunction()
  }, 1000)
}

watch(
  needToRefreshEula,
  newValue => {
    showEulaDialog.value = newValue
  },
  { immediate: true }
)

const selectedInstanceId = computed(() => {
  return instanceStore.selectedInstanceId
})
const canCloseVersionWarning = computed(() => {
  return closeTimer.value <= 0
})

const isUpdateAvailable = computed(() => {
  return miscellaneousStore.isUpdateAvailable
})

function update() {
  try {
    const updateHandler = miscellaneousStore.getUpdateHandler
    updateHandler()
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
  }
}

const eulaTargetGUID = computed(() => {
  return instanceStore.selectedInstanceId ? instanceStore.selectedInstanceId : instanceStore.getSenator.globalUniqueIdentifier
})

async function goToOverview() {
  return router.push({ name: 'Quick Instance Overview' })
}

async function goToSettings() {
  return router.push({ name: 'Settings' })
}

async function goBack() {
  return router.push({ name: 'Instances' })
}

function setVersionWarning() {
  const value = useBranchString()
  versionWarningTitle.value = t('version.branches.' + value + '.title')
  versionWarningText.value = t('version.branches.' + value + '.text')
  switch (value) {
    case 'local':
      versionWarningColor.value = 'red'
      versionWarning.value = false
      useMiscellaneousStore().versionWarning = false
      break
    case 'dev':
      versionWarningColor.value = 'red'
      versionWarning.value = true
      useMiscellaneousStore().versionWarning = true
      break
    case 'staging':
      versionWarningColor.value = 'red'
      versionWarning.value = true
      useMiscellaneousStore().versionWarning = true
      break
    case 'production':
      versionWarning.value = false
      useMiscellaneousStore().versionWarning = false
      break
    default:
      versionWarning.value = false
      useMiscellaneousStore().versionWarning = false
  }
  closeTimer.value = 10
  canCloseTimerFunction()
}

function startVersionWarningTimer() {
  const min = 1000 * 20
  const max = 1000 * 60 * 10
  const timeout = Math.floor(Math.random() * (max - min + 1) + min)
  setTimeout(() => {
    setVersionWarning()
  }, timeout)
}

function goToHelpDesk() {
  window.open('https://vetvise.atlassian.net/servicedesk/customer/portal/8/group/21/create/66', '_blank', 'noopener,noreferrer')
}

onMounted(() => {
  locale.value = settingsStore.language
  setVersionWarning()
  showLanguageDialog.value = !settingsStore.languageSelected
  showWelcomeDialog.value = true
})

watch(
  () => settingsStore.theme,
  newTheme => {
    theme.change(newTheme)
  },
  { immediate: true }
)
</script>

<template>
  <v-theme-provider
    :theme="settingsStore.theme"
    :with-background="true"
  >
    <QueueInvestigationCard v-if="settingsStore.queueDebugOutput" />
    <v-app>
      <GenericCustomDialog
        v-model="showLanguageDialog"
        :persistent="true"
        :hide-close-button="true"
        :hide-cancel-button="true"
        max-width="400px"
        width="100%"
      >
        <template #headerIcon>
          <v-icon icon="mdi-translate-variant" />
        </template>
        <template #title>
          {{ $t('settings.select_language') }}
        </template>
        <template #body>
          <LanguageSelector hide-details />
        </template>
        <template #actions>
          <v-btn
            class="w-100"
            color="success"
            variant="flat"
            :text="$t('general_interface.buttons.confirm')"
            @click="
              () => {
                settingsStore.languageSelected = true
                showLanguageDialog = false
              }
            "
          />
        </template>
      </GenericCustomDialog>

      <GenericCustomDialog
        v-if="canShowWelcomeDialog"
        v-model="showWelcomeDialog"
        :persistent="true"
        hide-close-button
        hide-cancel-button
        max-width="600"
        width="100%"
      >
        <template #headerIcon>
          <v-icon icon="mdi-human-greeting-variant" />
        </template>
        <template #title>
          {{ $t('welcome.title', { appName: useProductName(false) }) }}
        </template>
        <template #body>
          {{ $t('welcome.text', { appName: useProductName(false) }) }}
        </template>
        <template #actions>
          <v-btn
            variant="outlined"
            append-icon="mdi-help-circle-outline"
            @click="goToHelpDesk"
          >
            {{ $t('welcome.need_help') }}
          </v-btn>
          <v-btn
            color="success"
            variant="flat"
            append-icon="mdi-arrow-right"
            @click="
              () => {
                $router.push({ name: 'Add Instance' })
                showWelcomeDialog = false
              }
            "
          >
            {{ $t('general_interface.buttons.continue') }}
          </v-btn>
        </template>
      </GenericCustomDialog>

      <v-btn
        v-if="isSimulatingPermissions"
        size="large"
        variant="flat"
        rounded="circle"
        style="position: sticky; top: 24px; left: 24px; z-index: 10000"
        color="warning"
        icon
        @click="showPermissionSimulationDialog = true"
      >
        <v-icon
          icon="mdi-alert"
          size="large"
        />
      </v-btn>

      <GenericCustomDialog v-model="showPermissionSimulationDialog">
        <template #headerIcon>
          <v-icon icon="mdi-alert" />
        </template>
        <template #title>
          {{ $t('settings.simulate_permissions') }}
        </template>
        <template #body>
          <v-alert type="info">
            {{ $t('settings.permission_simulation_enabled') }}
          </v-alert>
          <SimulatedPermissionPicker />

          <v-btn
            class="ma-2"
            variant="flat"
            color="primary"
            prepend-icon="mdi-cancel"
            @click="
              () => {
                settingsStore.simulatePermissions = false
                showPermissionSimulationDialog = false
              }
            "
          >
            {{ $t('settings.disable_permission_simulation') }}
          </v-btn>
        </template>
      </GenericCustomDialog>

      <v-app-bar
        v-if="versionWarning && !mobile"
        density="compact"
        location="top"
        class="my-0 py-0"
        :color="versionWarningColor"
      >
        <div class="w-100 rainbow">
          <v-row
            class="justify-space-between mx-2 my-0 py-0 align-center"
            density="comfortable"
          >
            <v-col cols="auto">
              <v-chip class="font-weight-black text-capitalize">
                {{ versionWarningTitle }}
              </v-chip>
            </v-col>
            <v-col cols="auto">
              <span class="font-weight-black">{{ versionWarningText }}</span>
            </v-col>
            <v-col
              cols="auto"
              class="justify-end"
            >
              <v-btn
                v-if="!canCloseVersionWarning"
                :disabled="true"
                variant="flat"
                size="small"
                icon="true"
              >
                {{ closeTimer }}
              </v-btn>
              <v-btn
                v-if="canCloseVersionWarning"
                icon="mdi-close"
                size="small"
                @click="
                  () => {
                    versionWarning = false
                    startVersionWarningTimer()
                  }
                "
              />
            </v-col>
          </v-row>
        </div>
      </v-app-bar>

      <v-app-bar v-if="showGlobalAppBar">
        <v-app-bar-nav-icon
          v-if="displayBackButton"
          class="rounded-circle"
          @click="goBack"
        >
          <v-icon
            icon="mdi-backburger"
            size="large"
          />
        </v-app-bar-nav-icon>

        <router-link :to="{ name: 'Instances' }">
          <v-avatar
            v-if="!isDataCollector"
            class="cursor-pointer mx-4"
            image="@/assets/logo_circle_white.png"
            :title="$t('barn_dashboard.go_to_instance_selection')"
            @click="router.push({ name: 'Instances' })"
          />
        </router-link>
        <v-app-bar-title class="mx-0">
          {{ useProductName(true) }}
        </v-app-bar-title>

        <v-btn
          :icon="true"
          class="rounded-circle"
        >
          <v-icon icon="mdi-dots-vertical" />
          <v-menu activator="parent">
            <v-sheet>
              <v-list class="py-0">
                <v-list-item
                  v-if="!isDataCollector && $route.name !== 'Quick Instance Overview'"
                  :title="$t('router_elements.overview')"
                  prepend-icon="mdi-table-large"
                  @click="goToOverview"
                />
                <v-list-item
                  v-if="$route.name !== 'Settings'"
                  :title="$t('router_elements.settings')"
                  prepend-icon="mdi-wrench"
                  @click="goToSettings"
                />
                <v-list-item
                  v-if="$route.name !== 'Instances'"
                  :title="$t('barn_dashboard.go_to_instance_selection')"
                  prepend-icon="mdi-backburger"
                  base-color="primary"
                  variant="tonal"
                  @click="router.push({ name: 'Instances' })"
                />
              </v-list>
            </v-sheet>
          </v-menu>
        </v-btn>
      </v-app-bar>

      <GenericCustomDialog
        v-if="mobile"
        v-model="versionWarning"
        :persistent="true"
        hide-cancel-button
        max-width="400px"
        width="100%"
        card-class="rainbow"
        :card-props="{ color: versionWarningColor }"
        hide-close-button
      >
        <template #headerIcon>
          <v-icon icon="mdi-alert" />
        </template>
        <template #title>
          {{ versionWarningTitle }}
        </template>
        <template #body>
          <div class="w-100 d-flex flex-column ga-6">
            {{ versionWarningText }}
          </div>
        </template>
        <template #actions>
          <v-btn
            v-if="!canCloseVersionWarning"
            :disabled="true"
            variant="flat"
            size="small"
            icon="true"
          >
            {{ closeTimer }}
          </v-btn>
          <v-btn
            v-if="canCloseVersionWarning"
            variant="outlined"
            @click="
              () => {
                versionWarning = false
                startVersionWarningTimer()
              }
            "
          >
            {{ $t('general_interface.buttons.close') }}
          </v-btn>
        </template>
      </GenericCustomDialog>

      <v-snackbar
        v-if="isUpdateAvailable"
        color="primary"
        :timeout="-1"
        :model-value="true"
        variant="elevated"
        :close-on-back="false"
        :close-on-content-click="false"
        :persistent="true"
      >
        <template #text>
          {{ $t('general_interface.update_available') }}
        </template>

        <template #actions>
          <v-btn
            variant="outlined"
            @click="update"
          >
            {{ $t('general_interface.buttons.update') }}
          </v-btn>
        </template>
      </v-snackbar>

      <GenericCustomDialog
        v-if="needToRefreshEula"
        v-model="showEulaDialog"
        :persistent="true"
        max-width="500px"
        width="100%"
        :card-props="{ loading: eulaLoading }"
        hide-close-button
        hide-cancel-button
      >
        <template #headerIcon>
          <v-icon icon="mdi-file-document-outline" />
        </template>
        <template #title>
          {{ $t('eula.title') }}
        </template>
        <template #body>
          <div class="w-100">
            {{ $t('eula.text') }}
            <EulaCheckbox
              v-model:accepted="hasAcceptedEULA"
              v-model:version="acceptedEula"
              :global-unique-identifier="eulaTargetGUID"
            />
          </div>
        </template>
        <template #actions>
          <v-btn
            :disabled="!hasAcceptedEULA"
            variant="elevated"
            color="success"
            @click="acceptEula"
          >
            {{ $t('eula.accept') }}
          </v-btn>
        </template>
      </GenericCustomDialog>

      <v-main
        :style="{
          backgroundRepeat: 'repeat',
          background: `${backgroundImage} scroll, linear-gradient(155deg, rgba(0, 0, 0, 0) 0%, ${hexToRGBA(theme.current.value.colors.secondary, 0.4)} 100%) fixed`,
        }"
      >
        <!-- Mobile-only pull-to-refresh spinner -->
        <div
          v-if="isNative()"
          id="pull-refresh"
          ref="pullRefreshElement"
          class="d-flex flex-column align-center justify-center overflow-hidden"
        >
          <span>
            <v-fade-transition>
              <v-icon
                icon="mdi-arrow-down"
                size="small"
                :style="{
                  transform: isRefreshing ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-in-out',
                }"
              />
            </v-fade-transition>
            {{ $t('general_interface.pull_to_refresh') }}
            <v-fade-transition>
              <v-icon
                icon="mdi-arrow-down"
                size="small"
                :style="{
                  transform: isRefreshing ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-out',
                }"
              />
            </v-fade-transition>
          </span>
          <v-progress-circular
            :model-value="refreshValue"
            class="my-2"
            :indeterminate="isRefreshing"
            color="primary"
            style="min-height: 10px"
          />
        </div>
        <router-view :key="selectedInstanceId" />
      </v-main>
    </v-app>
  </v-theme-provider>
</template>
